import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldAlert } from 'lucide-react';

import { Button, Dialog, DialogFooter } from '@/shared/ui';
import {
  ASSIGNABLE_ROLES,
  getUserDisplayName,
  ROLE_LABEL_KEY,
} from '@entities/User';
import type { User, UserRole } from '@psp-aggregator/sdk';

export interface AssignRoleModalProps {
  onClose: () => void;
  onConfirm: (role: UserRole) => Promise<void>;
  submitting?: boolean;
  user: User;
}

export const AssignRoleModal: FC<AssignRoleModalProps> = ({
  onClose,
  onConfirm,
  submitting = false,
  user,
}) => {
  const { t } = useTranslation();
  const [role, setRole] = useState<UserRole | ''>('');
  const chosen = ASSIGNABLE_ROLES.find((item) => item.value === role);

  return (
    <Dialog title={t('pages.admin.users.modals.assignTitle')} onClose={onClose}>
      <p className="mb-3 text-sm text-muted-foreground">
        {t('pages.admin.users.modals.assignDescription', {
          name: getUserDisplayName(user),
        })}
      </p>

      <div className="mb-4 space-y-2">
        {ASSIGNABLE_ROLES.map((item) => (
          <button
            key={item.value}
            className={`flex w-full cursor-pointer items-center justify-between rounded-xl border px-3.5 py-2.5 text-sm transition-colors ${
              role === item.value
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border hover:bg-muted'
            }`}
            type="button"
            onClick={() => setRole(item.value)}>
            <span className="font-medium">{t(item.labelKey)}</span>
            {item.internal && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
                {t('roles.internal')}
              </span>
            )}
          </button>
        ))}
      </div>

      {!!chosen?.internal && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            {t('pages.admin.users.modals.internalWarning', {
              role: t(chosen.labelKey),
            })}
          </p>
        </div>
      )}

      <DialogFooter
        endSlot={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('buttons.cancel')}
            </Button>
            <Button
              disabled={!role || submitting}
              type="button"
              variant="brand"
              onClick={() => {
                if (!role) {
                  return;
                }

                void onConfirm(role);
              }}>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('buttons.confirm')
              )}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

AssignRoleModal.displayName = 'AssignRoleModal';

export interface ChangeRoleModalProps {
  currentRole: UserRole;
  onClose: () => void;
  onConfirm: (role: UserRole) => Promise<void>;
  options: readonly UserRole[];
  submitting?: boolean;
  user: User;
}

export const ChangeRoleModal: FC<ChangeRoleModalProps> = ({
  currentRole,
  onClose,
  onConfirm,
  options,
  submitting = false,
  user,
}) => {
  const { t } = useTranslation();
  const [role, setRole] = useState<UserRole | ''>('');

  return (
    <Dialog title={t('pages.admin.users.modals.changeTitle')} onClose={onClose}>
      <p className="mb-3 text-sm text-muted-foreground">
        {t('pages.admin.users.modals.changeDescription', {
          currentRole: t(ROLE_LABEL_KEY[currentRole]),
          name: getUserDisplayName(user),
        })}
      </p>

      <div className="mb-4 space-y-2">
        {options.map((option) => (
          <button
            key={option}
            className={`w-full cursor-pointer rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-colors ${
              role === option
                ? 'border-primary bg-primary/10'
                : 'border-border hover:bg-muted'
            }`}
            type="button"
            onClick={() => setRole(option)}>
            {t(ROLE_LABEL_KEY[option])}
          </button>
        ))}
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {t('pages.admin.users.modals.changeWarning')}
        </p>
      </div>

      <DialogFooter
        endSlot={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('buttons.cancel')}
            </Button>
            <Button
              disabled={!role || submitting}
              type="button"
              variant="brand"
              onClick={() => {
                if (!role) {
                  return;
                }

                void onConfirm(role);
              }}>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('buttons.confirm')
              )}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

ChangeRoleModal.displayName = 'ChangeRoleModal';
